import React, { useEffect, useState } from "react";
import {
    Box, Flex, Text, Button, Textarea, VStack, SimpleGrid,
    Icon, useColorModeValue, RadioGroup, Radio, Stack,
    Select, Input,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import {
    CheckCircle2, PlaneLanding, Car,
    TrainFront, Bus, Clock, User, Lock,
} from "lucide-react";
import axios from "axios";
import BeatLoader from "react-spinners/BeatLoader";

const API = process.env.REACT_APP_API;

const HOURS   = Array.from({ length: 12 }, (_, i) => String(i + 12).padStart(2, "0")); // 12–23
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")); // 00,05,…,55

const METHODS = [
    { value: "flight", label: "Aereo",   icon: PlaneLanding },
    { value: "car",    label: "Auto",    icon: Car },
    { value: "train",  label: "Treno",   icon: TrainFront },
    { value: "bus",    label: "Autobus", icon: Bus },
];

function InfoRow({ label, value }) {
    const labelColor = useColorModeValue("gray.500", "whiteAlpha.600");
    if (!value) return null;
    return (
        <Flex align="baseline" gap="8px" py="5px" borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="13px" fontWeight="600" color={labelColor} minW="100px" textTransform="uppercase" letterSpacing="0.05em">
                {label}
            </Text>
            <Text fontSize="14px" color="gray.800">{value}</Text>
        </Flex>
    );
}

export default function ArrivalRequest() {
    const { token } = useParams();

    const pageBg    = useColorModeValue("gray.50",  "navy.900");
    const cardBg    = useColorModeValue("white",    "navy.800");
    const borderClr = useColorModeValue("gray.200", "whiteAlpha.100");
    const mutedClr  = useColorModeValue("gray.500", "whiteAlpha.600");
    const labelClr  = useColorModeValue("gray.600", "whiteAlpha.600");

    const [loading,       setLoading]       = useState(true);
    const [submitting,    setSubmitting]    = useState(false);
    const [error,         setError]         = useState(null);
    const [justSubmitted, setJustSubmitted] = useState(false);
    const [locked,        setLocked]        = useState(false);
    const [expired,       setExpired]       = useState(false);
    const [editMode,      setEditMode]      = useState(false);
    const [data,          setData]          = useState(null);

    // form state
    const [hour,        setHour]        = useState("");
    const [minute,      setMinute]      = useState("00");
    const [method,      setMethod]      = useState("");
    const [transportId, setTransportId] = useState("");
    const [comments,    setComments]    = useState("");

    useEffect(() => {
        axios.get(`${API}/reservation/arrival/${token}`)
            .then(res => {
                setData(res.data);
                setLocked(res.data.locked);
                setExpired(res.data.expired);
                // Pre-fill form with any previously saved data
                if (res.data.arrival_time) {
                    const [h, m] = res.data.arrival_time.split(":");
                    setHour(h);
                    setMinute(m);
                }
                if (res.data.arrival_method) setMethod(res.data.arrival_method);
                if (res.data.transport_id)   setTransportId(res.data.transport_id);
                if (res.data.comments)       setComments(res.data.comments);
                setLoading(false);
            })
            .catch(() => {
                setError("Questo link di arrivo non è valido o è scaduto.");
                setLoading(false);
            });
    }, [token]);

    const needsTransport = method === "flight" || method === "train";
    const canSubmit = hour !== "" && method !== "" && (!needsTransport || transportId.trim() !== "");

    const handleSubmit = () => {
        if (!canSubmit) return;
        setSubmitting(true);
        axios.post(`${API}/reservation/arrival/${token}`, {
            arrival_time:   `${hour}:${minute}`,
            arrival_method: method,
            transport_id:   needsTransport ? transportId.trim() : null,
            comments:       comments || null,
        })
            .then(() => {
                if (!wasSubmitted) {
                    setJustSubmitted(true);
                } else {
                    setData(d => ({
                        ...d,
                        submitted:      true,
                        arrival_time:   `${hour}:${minute}`,
                        arrival_method: method,
                        transport_id:   needsTransport ? transportId.trim() : null,
                        comments:       comments || null,
                    }));
                }
                setEditMode(false);
                setSubmitting(false);
            })
            .catch(err => {
                const msg = err.response?.data?.message || "Invio non riuscito. Riprova.";
                setError(msg);
                setSubmitting(false);
            });
    };

    // ── Loading ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Flex minH="60vh" align="center" justify="center">
                <BeatLoader size={12} color="#422CFA" />
            </Flex>
        );
    }

    // ── Error ──────────────────────────────────────────────────────────────────
    if (error && !data) {
        return (
            <Flex minH="60vh" align="center" justify="center" px="20px">
                <Box textAlign="center" maxW="420px">
                    <Text fontSize="18px" fontWeight="700" color="gray.700" mb="8px">Link Non Disponibile</Text>
                    <Text fontSize="14px" color="gray.500">{error}</Text>
                </Box>
            </Flex>
        );
    }

    const reservation  = data?.reservation;
    const contactEmail = data?.contact_email;
    const contactPhone = data?.contact_phone;
    const wasSubmitted = data?.submitted;

    // ── Expired: check-in date has fully passed ────────────────────────────────
    if (expired) {
        return (
            <Flex minH="60vh" align="center" justify="center" px={{ base: "16px", md: "20px" }}>
                <Box
                    bg={cardBg}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={borderClr}
                    p={{ base: "28px", md: "40px" }}
                    maxW="480px"
                    w="100%"
                    textAlign="center"
                >
                    <Icon as={Lock} boxSize="48px" color="gray.400" mb="16px" />
                    <Text fontSize="20px" fontWeight="700" color="gray.800" mb="8px">
                        Link Scaduto
                    </Text>
                    <Text fontSize="14px" color="gray.500" lineHeight="1.7">
                        Questo link è scaduto. Se hai bisogno di assistenza, contattaci direttamente:
                    </Text>
                    <Box mt="16px" textAlign="left" display="inline-block">
                        {contactEmail && <Text fontSize="14px" fontWeight="600" color="gray.700" mb="6px">✉️ {contactEmail}</Text>}
                        {contactPhone && <Text fontSize="14px" fontWeight="600" color="gray.700">📞 {contactPhone}</Text>}
                    </Box>
                </Box>
            </Flex>
        );
    }

    // ── Locked: submitted but past 12h cutoff — show their arrival time ────────
    if (wasSubmitted && locked) {
        return (
            <Flex minH="60vh" align="center" justify="center" px={{ base: "16px", md: "20px" }}>
                <Box
                    bg={cardBg}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={borderClr}
                    p={{ base: "28px", md: "40px" }}
                    maxW="480px"
                    w="100%"
                    textAlign="center"
                >
                    <Icon as={CheckCircle2} boxSize="48px" color="green.400" mb="16px" />
                    <Text fontSize="20px" fontWeight="700" color="gray.800" mb="16px">
                        Dati di Arrivo Ricevuti
                    </Text>
                    {data?.arrival_time && (
                        <Box mb="20px" textAlign="left" display="inline-block">
                            <Text fontSize="13px" fontWeight="700" color={labelClr} textTransform="uppercase" letterSpacing="0.08em" mb="10px">I Tuoi Dati</Text>
                            <Flex align="center" gap="8px" mb="8px">
                                <Icon as={Clock} color={mutedClr} boxSize="16px" />
                                <Text fontSize="16px" fontWeight="600" color="gray.800">{data.arrival_time}</Text>
                            </Flex>
                            {data.arrival_method && (() => {
                                const m = METHODS.find(m => m.value === data.arrival_method);
                                return m ? (
                                    <Flex align="center" gap="8px" mb="8px">
                                        <Icon as={m.icon} color={mutedClr} boxSize="16px" />
                                        <Text fontSize="16px" fontWeight="600" color="gray.800">{m.label}</Text>
                                    </Flex>
                                ) : null;
                            })()}
                            {data.transport_id && (
                                <Text fontSize="14px" color="gray.700" mb="4px">
                                    🎫 {data.transport_id}
                                </Text>
                            )}
                            {data.comments ? (
                                <Text fontSize="14px" color="gray.600" mt="4px" fontStyle="italic">"{data.comments}"</Text>
                            ) : (
                                <Text fontSize="13px" color={mutedClr} mt="4px">Nessuna nota aggiuntiva</Text>
                            )}
                        </Box>
                    )}
                    <Box p="14px" bg="orange.50" borderRadius="12px" border="1px solid" borderColor="orange.200">
                        <Flex align="center" gap="6px" justify="center" mb="6px">
                            <Icon as={Lock} color="orange.500" boxSize="14px" />
                            <Text fontSize="13px" fontWeight="600" color="orange.600">Non è più possibile modificare i dati</Text>
                        </Flex>
                        <Text fontSize="13px" color="gray.600" lineHeight="1.7">
                            Se hai bisogno di apportare modifiche, contattaci:
                        </Text>
                        {contactEmail && <Text fontSize="13px" fontWeight="600" color="gray.700" mt="6px">✉️ {contactEmail}</Text>}
                        {contactPhone && <Text fontSize="13px" fontWeight="600" color="gray.700">📞 {contactPhone}</Text>}
                    </Box>
                </Box>
            </Flex>
        );
    }

    // ── Locked: not submitted and past 8h cutoff ───────────────────────────────
    if (!wasSubmitted && locked) {
        return (
            <Flex minH="60vh" align="center" justify="center" px={{ base: "16px", md: "20px" }}>
                <Box
                    bg={cardBg}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={borderClr}
                    p={{ base: "28px", md: "40px" }}
                    maxW="480px"
                    w="100%"
                    textAlign="center"
                >
                    <Icon as={Lock} boxSize="48px" color="orange.400" mb="16px" />
                    <Text fontSize="20px" fontWeight="700" color="gray.800" mb="8px">
                        Invio Chiuso
                    </Text>
                    <Text fontSize="14px" color="gray.500" lineHeight="1.7" mb="20px">
                        Non avendo ricevuto i tuoi dati, ti aspettiamo durante il nostro orario di check-in standard (16:00–20:00).<br />
                        Per richieste urgenti al di fuori di questi orari, contattaci direttamente:
                    </Text>
                    <Box textAlign="left" display="inline-block">
                        {contactEmail && <Text fontSize="14px" fontWeight="600" color="gray.700" mb="6px">✉️ {contactEmail}</Text>}
                        {contactPhone && <Text fontSize="14px" fontWeight="600" color="gray.700">📞 {contactPhone}</Text>}
                    </Box>
                </Box>
            </Flex>
        );
    }

    // ── Just submitted (transient success screen) ─────────────────────────────
    if (justSubmitted) {
        return (
            <Flex minH="60vh" align="center" justify="center" px="20px">
                <Box textAlign="center" maxW="460px">
                    <Icon as={CheckCircle2} boxSize="56px" color="green.400" mb="16px" />
                    <Text fontSize="20px" fontWeight="700" color="gray.800" mb="8px">
                        Dati di Arrivo Ricevuti
                    </Text>
                    <Text fontSize="14px" color="gray.500" lineHeight="1.7">
                        Grazie, <strong>{reservation?.name}</strong>. Abbiamo ricevuto i tuoi dati di arrivo e saremo pronti ad accoglierti.
                    </Text>
                    <Button mt="20px" variant="outline" colorScheme="brand" size="sm" onClick={() => setJustSubmitted(false)}>
                        Modifica i miei dati
                    </Button>
                </Box>
            </Flex>
        );
    }

    // ── Main form ──────────────────────────────────────────────────────────────
    return (
        <Box px={{ base: "16px", md: "48px" }} py={{ base: "24px", md: "40px" }} maxW="1100px" mx="auto">
            <Box mb="28px">
                <Text fontSize="24px" fontWeight="700" color="gray.800">Dettagli di Arrivo</Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap="24px" alignItems="start">

                {/* ── Left: reservation info ── */}
                <Box
                    bg={cardBg}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={borderClr}
                    p={{ base: "24px", md: "32px" }}
                >
                    <Text fontSize="13px" fontWeight="700" color={labelClr} textTransform="uppercase" letterSpacing="0.08em" mb="16px">
                        Riepilogo Prenotazione
                    </Text>

                    {reservation?.property && (
                        <Text fontSize="20px" fontWeight="700" color="gray.800" mb="4px">
                            {reservation.property}
                        </Text>
                    )}

                    <VStack spacing={0} align="stretch" mt="12px" mb="20px">
                        <InfoRow label="Ospite"   value={reservation?.name} />
                        <InfoRow label="Check-in"  value={reservation?.check_in} />
                        <InfoRow label="Check-out" value={reservation?.check_out} />
                        {reservation?.adults && (
                            <InfoRow label="Ospiti" value={`${reservation.adults} ${reservation.adults > 1 ? 'adulti' : 'adulto'}${reservation.children ? ` · ${reservation.children} ${reservation.children > 1 ? 'bambini' : 'bambino'}` : ''}`} />
                        )}
                    </VStack>

                    <Flex align="center" gap="8px" mt="8px">
                        <Icon as={User} color={mutedClr} boxSize="15px" />
                        <Text fontSize="13px" color={mutedClr}>
                            {reservation?.name}
                        </Text>
                    </Flex>
                </Box>

                {/* ── Right: arrival details ── */}
                <Box
                    bg={cardBg}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor={borderClr}
                    p={{ base: "24px", md: "32px" }}
                >
                    <Text fontSize="13px" fontWeight="700" color={labelClr} textTransform="uppercase" letterSpacing="0.08em" mb="20px">
                        Dettagli di Arrivo
                    </Text>

                    {wasSubmitted && !editMode ? (
                        /* ── Read-only view ── */
                        <>
                            {/* Time */}
                            <Box mb="20px">
                                <Flex align="center" gap="6px" mb="6px">
                                    <Icon as={Clock} color={labelClr} boxSize="15px" />
                                    <Text fontSize="1rem" fontWeight="600" color="gray.700">
                                        Orario stimato di arrivo a Firenze
                                    </Text>
                                </Flex>
                                <Text fontSize="1rem" color="gray.800" ps="21px">{hour}:{minute}</Text>
                            </Box>

                            {/* Method */}
                            <Box mb="20px">
                                <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="6px">
                                    Come arriverai a Firenze?
                                </Text>
                                {(() => {
                                    const m = METHODS.find(m => m.value === method);
                                    return m ? (
                                        <Flex align="center" gap="8px">
                                            <Icon as={m.icon} boxSize="18px" color="brand.500" />
                                            <Text fontSize="1rem" color="gray.800">{m.label}</Text>
                                        </Flex>
                                    ) : null;
                                })()}
                            </Box>

                            {/* Transport number */}
                            {(method === "flight" || method === "train") && (
                                <Box mb="20px">
                                    <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="6px">
                                        {method === "flight" ? "Numero del Volo" : "Numero del Treno"}
                                    </Text>
                                    <Text fontSize="1rem" color="gray.800" ps="0">{transportId || "—"}</Text>
                                </Box>
                            )}

                            {/* Comments */}
                            {comments && (
                                <Box mb="20px">
                                    <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="6px">
                                        Note Aggiuntive
                                    </Text>
                                    <Text fontSize="1rem" color="gray.800" whiteSpace="pre-wrap">{comments}</Text>
                                </Box>
                            )}

                            <Button
                                variant="outline"
                                colorScheme="brand"
                                width="100%"
                                size="lg"
                                borderRadius="12px"
                                mt="4px"
                                onClick={() => setEditMode(true)}
                            >
                                Modifica
                            </Button>
                        </>
                    ) : (
                        /* ── Edit form ── */
                        <>
                            {/* Arrival time */}
                            <Box mb="24px">
                                <Flex align="center" gap="6px" mb="8px">
                                    <Icon as={Clock} color={labelClr} boxSize="15px" />
                                    <Text fontSize="1rem" fontWeight="600" color="gray.700">
                                        Orario stimato di arrivo a Firenze <Box as="span" color="red.500">*</Box>
                                    </Text>
                                </Flex>
                                <Flex gap="10px" align="center">
                                    <Select
                                        placeholder="Ora"
                                        value={hour}
                                        onChange={e => setHour(e.target.value)}
                                        size="md"
                                        borderRadius="10px"
                                        maxW="120px"
                                    >
                                        {HOURS.map(h => (
                                            <option key={h} value={h}>{h}</option>
                                        ))}
                                    </Select>
                                    <Text fontWeight="700" color="gray.500" fontSize="18px">:</Text>
                                    <Select
                                        value={minute}
                                        onChange={e => setMinute(e.target.value)}
                                        size="md"
                                        borderRadius="10px"
                                        maxW="120px"
                                    >
                                        {MINUTES.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </Select>
                                </Flex>
                            </Box>

                            {/* Arrival method */}
                            <Box mb="24px">
                                <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="12px">
                                    Come arriverai a Firenze? <Box as="span" color="red.500">*</Box>
                                </Text>
                                <RadioGroup value={method} onChange={setMethod}>
                                    <Stack spacing="10px">
                                        {METHODS.map(m => (
                                            <Box
                                                key={m.value}
                                                as="label"
                                                display="flex"
                                                alignItems="center"
                                                gap="10px"
                                                p="12px 16px"
                                                borderRadius="10px"
                                                border="1px solid"
                                                borderColor={method === m.value ? "brand.500" : borderClr}
                                                bg={method === m.value ? "brand.50" : "transparent"}
                                                cursor="pointer"
                                                transition="all 0.15s"
                                            >
                                                <Radio value={m.value} colorScheme="brand" />
                                                <Icon as={m.icon} boxSize="18px" color={method === m.value ? "brand.500" : mutedClr} />
                                                <Text fontSize="14px" fontWeight={method === m.value ? "600" : "400"} color="gray.700">
                                                    {m.label}
                                                </Text>
                                            </Box>
                                        ))}
                                    </Stack>
                                </RadioGroup>
                            </Box>

                            {/* Transport number — flight or train only */}
                            {needsTransport && (
                                <Box mb="24px">
                                    <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="8px">
                                        {method === "flight" ? "Numero del Volo" : "Numero del Treno"} <Box as="span" color="red.500">*</Box>
                                    </Text>
                                    <Input
                                        value={transportId}
                                        onChange={e => setTransportId(e.target.value.toUpperCase())}
                                        placeholder={method === "flight" ? "es. FR1234" : "es. IC509"}
                                        borderRadius="10px"
                                        maxLength={20}
                                        textTransform="uppercase"
                                    />
                                </Box>
                            )}

                            {/* Comments */}
                            <Box mb="28px">
                                <Text fontSize="1rem" fontWeight="600" color="gray.700" mb="8px">
                                    Note Aggiuntive <Box as="span" color={mutedClr} fontWeight="400">(facoltativo)</Box>
                                </Text>
                                <Textarea
                                    value={comments}
                                    onChange={e => setComments(e.target.value)}
                                    placeholder="es. numero del volo, richieste particolari..."
                                    borderRadius="10px"
                                    rows={3}
                                    resize="none"
                                />
                            </Box>

                            {error && (
                                <Text fontSize="13px" color="red.500" mb="12px">{error}</Text>
                            )}

                            <Flex gap="10px">
                                {wasSubmitted && (
                                    <Button
                                        variant="outline"
                                        colorScheme="gray"
                                        flex={1}
                                        size="lg"
                                        borderRadius="12px"
                                        onClick={() => { setEditMode(false); setError(null); }}
                                    >
                                        Annulla
                                    </Button>
                                )}
                                <Button
                                    variant="brand"
                                    flex={1}
                                    size="lg"
                                    borderRadius="12px"
                                    isDisabled={!canSubmit}
                                    isLoading={submitting}
                                    onClick={handleSubmit}
                                    _disabled={{ opacity: 0.5, cursor: 'not-allowed', bg: 'brand.500', color: 'white', _hover: { bg: 'brand.500' } }}
                                >
                                    {wasSubmitted ? "Aggiorna i Dati di Arrivo" : "Invia i Dati di Arrivo"}
                                </Button>
                            </Flex>
                        </>
                    )}
                </Box>
            </SimpleGrid>
        </Box>
    );
}
